import { CameraIcon } from './ui/icons/CameraIcon'
import { GoogleMeetIcon } from './ui/icons/GoogleMeetIcon'
import { LocationIcon } from './ui/icons/LocationIcon'
import { PhoneIcon } from './ui/icons/PhoneIcon'

export enum EventLocationTypeEnum {
  GoogleMeet = 'GOOGLE_MEET',
  OnlineMeeting = 'ONLINE_MEETING',
  Personal = 'PERSONAL',
  Phone = 'PHONE',
  Zoom = 'ZOOM',
}

export enum EventTimeZoneTypeEnum {
  Fixed = 'FIXED',
  Local = 'LOCAL',
}

export enum BookingStatusEnum {
  Cancelled = 'CANCELLED',
  Past = 'PAST',
  Rescheduled = 'RESCHEDULED',
  Scheduled = 'SCHEDULED',
  Upcoming = 'UPCOMING',
}

export const weekdaysMap = {
  SUNDAY: 0,
  MONDAY: 1,
  TUESDAY: 2,
  WEDNESDAY: 3,
  THURSDAY: 4,
  FRIDAY: 5,
  SATURDAY: 6,
}

export const weekdayShortLocoKeys = {
  [weekdaysMap.SUNDAY]: 'components.core.calendar.weekdays.sunday_short',
  [weekdaysMap.MONDAY]: 'components.core.calendar.weekdays.monday_short',
  [weekdaysMap.TUESDAY]: 'components.core.calendar.weekdays.tuesday_short',
  [weekdaysMap.WEDNESDAY]: 'components.core.calendar.weekdays.wednesday_short',
  [weekdaysMap.THURSDAY]: 'components.core.calendar.weekdays.thursday_short',
  [weekdaysMap.FRIDAY]: 'components.core.calendar.weekdays.friday_short',
  [weekdaysMap.SATURDAY]: 'components.core.calendar.weekdays.saturday_short',
}

export const getReadableShortWeekdayName = (day: Date | number) => {
  if (typeof day === 'number') return weekdayShortLocoKeys[day]
  return weekdayShortLocoKeys[day.getDay()]
}

const eventLocationLabels = {
  [EventLocationTypeEnum.GoogleMeet]:
    'components.core.calendar.event_location.google_meet',
  [EventLocationTypeEnum.OnlineMeeting]:
    'components.core.calendar.event_location.online_meeting',
  [EventLocationTypeEnum.Personal]:
    'components.core.calendar.event_location.personal',
  [EventLocationTypeEnum.Phone]:
    'components.core.calendar.event_location.phone',
  [EventLocationTypeEnum.Zoom]: 'components.core.calendar.event_location.zoom',
}

const eventLocationIcon = {
  [EventLocationTypeEnum.GoogleMeet]: GoogleMeetIcon,
  [EventLocationTypeEnum.OnlineMeeting]: CameraIcon,
  [EventLocationTypeEnum.Personal]: LocationIcon,
  [EventLocationTypeEnum.Phone]: PhoneIcon,
  [EventLocationTypeEnum.Zoom]: CameraIcon,
}

export const getReadableEventLocationIcon = (
  location: EventLocationTypeEnum,
) => {
  return eventLocationIcon[location]
}

export const getReadableEventLocation = (location: EventLocationTypeEnum) => {
  return eventLocationLabels[location]
}

const monthsLocoKeys = {
  january: 'components.core.calendar.months.january',
  february: 'components.core.calendar.months.february',
  march: 'components.core.calendar.months.march',
  april: 'components.core.calendar.months.april',
  may: 'components.core.calendar.months.may',
  june: 'components.core.calendar.months.june',
  july: 'components.core.calendar.months.july',
  august: 'components.core.calendar.months.august',
  september: 'components.core.calendar.months.september',
  october: 'components.core.calendar.months.october',
  november: 'components.core.calendar.months.november',
  december: 'components.core.calendar.months.december',
}

export const getReadableMonthName = (date: Date) => {
  const month = date.toLocaleString('en-US', { month: 'long' })
  return monthsLocoKeys[month.toLowerCase() as keyof typeof monthsLocoKeys]
}

export const timeZones = [
  'America/Eirunepe',
  'America/Rio_Branco',
  'Asia/Kabul',
  'America/Anchorage',
  'America/Juneau',
  'America/Metlakatla',
  'America/Nome',
  'America/Sitka',
  'America/Yakutat',
  'America/Boa_Vista',
  'America/Campo_Grande',
  'America/Cuiaba',
  'America/Manaus',
  'America/Porto_Velho',
  'Asia/Anadyr',
  'Pacific/Apia',
  'Asia/Aden',
  'Asia/Baghdad',
  'Asia/Bahrain',
  'Asia/Kuwait',
  'Asia/Qatar',
  'Asia/Riyadh',
  'America/Buenos_Aires',
  'America/Catamarca',
  'America/Cordoba',
  'America/Jujuy',
  'America/Argentina/La_Rioja',
  'America/Mendoza',
  'America/Argentina/Rio_Gallegos',
  'America/Argentina/Salta',
  'America/Argentina/San_Juan',
  'America/Argentina/San_Luis',
  'America/Argentina/Tucuman',
  'America/Argentina/Ushuaia',
  'Asia/Yerevan',
  'America/Anguilla',
  'America/Antigua',
  'America/Aruba',
  'America/Barbados',
  'Atlantic/Bermuda',
  'America/Blanc-Sablon',
  'America/Curacao',
  'America/Dominica',
  'America/Glace_Bay',
  'America/Goose_Bay',
  'America/Grenada',
  'America/Guadeloupe',
  'America/Halifax',
  'America/Kralendijk',
  'America/Lower_Princes',
  'America/Marigot',
  'America/Martinique',
  'America/Moncton',
  'America/Montserrat',
  'America/Port_of_Spain',
  'America/Puerto_Rico',
  'America/Santo_Domingo',
  'America/St_Barthelemy',
  'America/St_Kitts',
  'America/St_Lucia',
  'America/St_Thomas',
  'America/St_Vincent',
  'America/Thule',
  'America/Tortola',
  'Australia/Eucla',
  'Asia/Baku',
  'Atlantic/Azores',
  'Asia/Dhaka',
  'Asia/Thimphu',
  'America/La_Paz',
  'America/Araguaina',
  'America/Bahia',
  'America/Belem',
  'America/Fortaleza',
  'America/Maceio',
  'America/Recife',
  'America/Santarem',
  'America/Sao_Paulo',
  'Asia/Brunei',
  'Atlantic/Cape_Verde',
  'Antarctica/Casey',
  'Africa/Blantyre',
  'Africa/Bujumbura',
  'Africa/Gaborone',
  'Africa/Harare',
  'Africa/Juba',
  'Africa/Khartoum',
  'Africa/Kigali',
  'Africa/Lubumbashi',
  'Africa/Lusaka',
  'Africa/Maputo',
  'Africa/Windhoek',
  'Australia/Adelaide',
  'Australia/Broken_Hill',
  'Australia/Darwin',
  'Africa/Algiers',
  'Europe/Amsterdam',
  'Europe/Andorra',
  'Europe/Belgrade',
  'Europe/Berlin',
  'Europe/Bratislava',
  'Europe/Brussels',
  'Europe/Budapest',
  'Europe/Busingen',
  'Africa/Ceuta',
  'Europe/Copenhagen',
  'Europe/Gibraltar',
  'Europe/Ljubljana',
  'Arctic/Longyearbyen',
  'Europe/Luxembourg',
  'Europe/Madrid',
  'Europe/Malta',
  'Europe/Monaco',
  'Europe/Oslo',
  'Europe/Paris',
  'Europe/Podgorica',
  'Europe/Prague',
  'Europe/Rome',
  'Europe/San_Marino',
  'Europe/Sarajevo',
  'Europe/Skopje',
  'Europe/Stockholm',
  'Europe/Tirane',
  'Africa/Tunis',
  'Europe/Vaduz',
  'Europe/Vatican',
  'Europe/Vienna',
  'Europe/Warsaw',
  'Europe/Zagreb',
  'Europe/Zurich',
  'Asia/Makassar',
  'CST6CDT',
  'America/Bahia_Banderas',
  'America/Belize',
  'America/North_Dakota/Beulah',
  'America/North_Dakota/Center',
  'America/Chicago',
  'America/Chihuahua',
  'America/Costa_Rica',
  'America/El_Salvador',
  'America/Guatemala',
  'America/Indiana/Knox',
  'America/Managua',
  'America/Matamoros',
  'America/Menominee',
  'America/Merida',
  'America/Mexico_City',
  'America/Monterrey',
  'America/North_Dakota/New_Salem',
  'America/Ojinaga',
  'America/Rankin_Inlet',
  'America/Regina',
  'America/Resolute',
  'America/Swift_Current',
  'America/Tegucigalpa',
  'America/Indiana/Tell_City',
  'America/Winnipeg',
  'Pacific/Guam',
  'Pacific/Saipan',
  'Pacific/Chatham',
  'Antarctica/Palmer',
  'America/Punta_Arenas',
  'America/Santiago',
  'Asia/Macau',
  'Asia/Shanghai',
  'Asia/Urumqi',
  'Indian/Christmas',
  'Pacific/Truk',
  'Indian/Cocos',
  'America/Bogota',
  'Pacific/Rarotonga',
  'Etc/UTC',
  'America/Havana',
  'Antarctica/Davis',
  'Antarctica/DumontDUrville',
  'Africa/Addis_Ababa',
  'Indian/Antananarivo',
  'Africa/Asmera',
  'Indian/Comoro',
  'Africa/Dar_es_Salaam',
  'Africa/Djibouti',
  'Africa/Kampala',
  'Indian/Mayotte',
  'Africa/Mogadishu',
  'Africa/Nairobi',
  'America/Scoresbysund',
  'Asia/Almaty',
  'Asia/Qostanay',
  'Asia/Dili',
  'Pacific/Easter',
  'Australia/Brisbane',
  'Australia/Hobart',
  'Australia/Lindeman',
  'Antarctica/Macquarie',
  'Australia/Melbourne',
  'Australia/Sydney',
  'Asia/Amman',
  'Europe/Athens',
  'Asia/Beirut',
  'Europe/Bucharest',
  'Africa/Cairo',
  'Europe/Chisinau',
  'Asia/Damascus',
  'Asia/Famagusta',
  'Asia/Gaza',
  'Asia/Hebron',
  'Europe/Helsinki',
  'Europe/Kaliningrad',
  'Europe/Kiev',
  'Europe/Mariehamn',
  'Asia/Nicosia',
  'Europe/Riga',
  'Europe/Sofia',
  'Europe/Tallinn',
  'Africa/Tripoli',
  'Europe/Vilnius',
  'Asia/Jayapura',
  'EST5EDT',
  'America/Coral_Harbour',
  'America/Cancun',
  'America/Cayman',
  'America/Detroit',
  'America/Grand_Turk',
  'America/Indianapolis',
  'America/Iqaluit',
  'America/Jamaica',
  'America/Louisville',
  'America/Indiana/Marengo',
  'America/Kentucky/Monticello',
  'America/Nassau',
  'America/New_York',
  'America/Panama',
  'America/Indiana/Petersburg',
  'America/Port-au-Prince',
  'America/Toronto',
  'America/Indiana/Vevay',
  'America/Indiana/Vincennes',
  'America/Indiana/Winamac',
  'America/Guayaquil',
  'Atlantic/Stanley',
  'America/Noronha',
  'Pacific/Fiji',
  'America/Cayenne',
  'Indian/Kerguelen',
  'Pacific/Galapagos',
  'Pacific/Gambier',
  'Asia/Tbilisi',
  'Pacific/Tarawa',
  'Etc/GMT',
  'Africa/Abidjan',
  'Africa/Accra',
  'Africa/Bamako',
  'Africa/Banjul',
  'Africa/Bissau',
  'Africa/Conakry',
  'Africa/Dakar',
  'America/Danmarkshavn',
  'Europe/Dublin',
  'Africa/Freetown',
  'Europe/Guernsey',
  'Europe/Isle_of_Man',
  'Europe/Jersey',
  'Africa/Lome',
  'Europe/London',
  'Africa/Monrovia',
  'Africa/Nouakchott',
  'Africa/Ouagadougou',
  'Atlantic/Reykjavik',
  'Africa/Sao_Tome',
  'Atlantic/St_Helena',
  'Antarctica/Troll',
  'Asia/Dubai',
  'Asia/Muscat',
  'America/Guyana',
  'America/Adak',
  'Pacific/Honolulu',
  'Asia/Hong_Kong',
  'Asia/Hovd',
  'Asia/Colombo',
  'Asia/Calcutta',
  'Indian/Chagos',
  'Asia/Bangkok',
  'Asia/Saigon',
  'Asia/Phnom_Penh',
  'Asia/Vientiane',
  'Asia/Tehran',
  'Asia/Irkutsk',
  'Asia/Jerusalem',
  'Asia/Tokyo',
  'Asia/Pyongyang',
  'Asia/Seoul',
  'Pacific/Kosrae',
  'Asia/Krasnoyarsk',
  'Asia/Novokuznetsk',
  'Asia/Bishkek',
  'Pacific/Kiritimati',
  'Australia/Lord_Howe',
  'Asia/Magadan',
  'Asia/Srednekolymsk',
  'Asia/Kuala_Lumpur',
  'Asia/Kuching',
  'Indian/Maldives',
  'Pacific/Marquesas',
  'Pacific/Kwajalein',
  'Pacific/Majuro',
  'Indian/Mauritius',
  'Antarctica/Mawson',
  'America/Hermosillo',
  'America/Mazatlan',
  'Europe/Moscow',
  'Europe/Astrakhan',
  'Europe/Minsk',
  'Europe/Saratov',
  'Europe/Simferopol',
  'Europe/Ulyanovsk',
  'MST7MDT',
  'America/Boise',
  'America/Cambridge_Bay',
  'America/Creston',
  'America/Dawson_Creek',
  'America/Denver',
  'America/Edmonton',
  'America/Fort_Nelson',
  'America/Inuvik',
  'America/Phoenix',
  'Asia/Rangoon',
  'Pacific/Nauru',
  'Asia/Katmandu',
  'Pacific/Noumea',
  'Pacific/Auckland',
  'Antarctica/McMurdo',
  'America/St_Johns',
  'Pacific/Niue',
  'Pacific/Norfolk',
  'Asia/Novosibirsk',
  'Asia/Omsk',
  'PST8PDT',
  'America/Los_Angeles',
  'America/Tijuana',
  'America/Vancouver',
  'Asia/Karachi',
  'Pacific/Palau',
  'Pacific/Bougainville',
  'Pacific/Port_Moresby',
  'America/Asuncion',
  'America/Lima',
  'Asia/Kamchatka',
  'Asia/Manila',
  'Pacific/Enderbury',
  'Pacific/Pitcairn',
  'Pacific/Ponape',
  'Indian/Reunion',
  'Antarctica/Rothera',
  'Asia/Barnaul',
  'Europe/Kirov',
  'Asia/Tomsk',
  'Asia/Sakhalin',
  'Europe/Samara',
  'Pacific/Midway',
  'Pacific/Pago_Pago',
  'Indian/Mahe',
  'Asia/Singapore',
  'Pacific/Guadalcanal',
  'Africa/Johannesburg',
  'Africa/Maseru',
  'Africa/Mbabane',
  'Atlantic/South_Georgia',
  'America/Miquelon',
  'America/Paramaribo',
  'Antarctica/Syowa',
  'Pacific/Tahiti',
  'Asia/Taipei',
  'Asia/Dushanbe',
  'Pacific/Fakaofo',
  'Pacific/Tongatapu',
  'Europe/Istanbul',
  'Asia/Ashgabat',
  'Pacific/Funafuti',
  'Asia/Ulaanbaatar',
  'Asia/Choibalsan',
  'America/Montevideo',
  'Asia/Samarkand',
  'Asia/Tashkent',
  'Pacific/Efate',
  'America/Caracas',
  'Asia/Vladivostok',
  'Asia/Ust-Nera',
  'Europe/Volgograd',
  'Antarctica/Vostok',
  'Pacific/Wake',
  'Pacific/Wallis',
  'Africa/Bangui',
  'Africa/Brazzaville',
  'Africa/Douala',
  'Africa/Kinshasa',
  'Africa/Lagos',
  'Africa/Libreville',
  'Africa/Luanda',
  'Africa/Malabo',
  'Africa/Ndjamena',
  'Africa/Niamey',
  'Africa/Porto-Novo',
  'America/Godthab',
  'Asia/Aqtau',
  'Asia/Aqtobe',
  'Asia/Atyrau',
  'Asia/Oral',
  'Asia/Qyzylorda',
  'Australia/Perth',
  'Atlantic/Canary',
  'Africa/Casablanca',
  'Africa/El_Aaiun',
  'Atlantic/Faeroe',
  'Europe/Lisbon',
  'Atlantic/Madeira',
  'Asia/Jakarta',
  'Asia/Pontianak',
  'Asia/Yakutsk',
  'Asia/Chita',
  'Asia/Khandyga',
  'Asia/Yekaterinburg',
  'America/Dawson',
  'America/Whitehorse',
  'UTC',
]

export enum BookingCalendarSteps {
  CalendarStep = 'calendarStep',
  FormStep = 'formStep',
  ThankYouStep = 'thankYouStep',
}

export enum TimeFormatEnum {
  h12 = 'h12',
  h24 = 'h23', // h24 includes 24:XX
}

export const timeFormatLabels = {
  [TimeFormatEnum.h12]: 'components.core.calendar.time_format.12h',
  [TimeFormatEnum.h24]: 'components.core.calendar.time_format.24h',
}
